import { useMemo, useState } from "react";
import { AlertTriangle, Users } from "lucide-react";
import { Button } from "@nexus/ui";
import { MAX_USERS_ACCESS_BATCH } from "@nexus/contracts";
import { useTranslation } from "react-i18next";
import type { UserAccessEntry } from "@/features/identity-hub/api/identity-hub.api";
import type { UserAccessPhase } from "@/features/identity-hub/lib/merge-users-access";
import { partitionRemoveAll } from "@/features/identity-hub/lib/remove-all-partition";

const CONFIRM_NAME_PREVIEW = 5;

interface RemoveAllRolesPanelProps {
  selectedUserIds: string[];
  accessByUser: Map<string, UserAccessEntry[]>;
  accessPhaseByUser: Map<string, UserAccessPhase>;
  loggedInUserId: string | null;
  userNames: Map<string, string>;
  onRemoveAll: (userIds: string[]) => void;
  onCancel: () => void;
  isPending: boolean;
}

/**
 * The body of RemoveAllRolesDialog: a review step with who
 * is excluded and why, then a confirm step naming who loses every role.
 */
export function RemoveAllRolesPanel({
  selectedUserIds, accessByUser, accessPhaseByUser, loggedInUserId, userNames,
  onRemoveAll, onCancel, isPending,
}: RemoveAllRolesPanelProps) {
  const { t } = useTranslation();
  const [isConfirming, setIsConfirming] = useState(false);
  const removal = useMemo(
    () => partitionRemoveAll(selectedUserIds, accessByUser, accessPhaseByUser, loggedInUserId),
    [selectedUserIds, accessByUser, accessPhaseByUser, loggedInUserId],
  );
  const count = removal.eligible.length;
  const isOverCap = count > MAX_USERS_ACCESS_BATCH;
  const canReview = count > 0 && removal.loading === 0 && !isOverCap;

  if (isConfirming) {
    const names = removal.eligible.slice(0, CONFIRM_NAME_PREVIEW).map((id) => userNames.get(id) ?? id);
    const more = count - names.length;
    return (
      <>
        <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-foreground">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
          <div className="flex flex-col gap-1">
            <p className="font-medium">
              {t("iam.dashboard.people.assignFixed.removeAllConfirm", {
                count,
                defaultValue: "Remove every role in every organization from {{count}} users? This can't be undone automatically.",
              })}
            </p>
            <p className="text-muted-foreground">
              {names.join(", ")}
              {more > 0 && t("iam.dashboard.people.assignFixed.removeAllMore", { count: more, defaultValue: " +{{count}} more" })}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" disabled={isPending} onClick={() => setIsConfirming(false)}>
            {t("common.back", "Back")}
          </Button>
          <Button size="sm" variant="destructive" disabled={isPending} onClick={() => onRemoveAll(removal.eligible)}>
            {isPending
              ? t("iam.dashboard.people.assignFixed.removeAllPending", "Removing…")
              : t("iam.dashboard.people.assignFixed.removeAllConfirmSubmit", "Remove all roles")}
          </Button>
        </div>
      </>
    );
  }

  const skips = [
    removal.skippedSelf > 0 && t("iam.dashboard.people.assignFixed.skippedSelf", "you are excluded"),
    removal.loading > 0 && t("iam.dashboard.people.assignFixed.skippedLoading", {
      count: removal.loading,
      defaultValue: "{{count}} still loading roles",
    }),
    removal.failed > 0 && t("iam.dashboard.people.assignFixed.skippedFailed", {
      count: removal.failed,
      defaultValue: "{{count}} couldn't load roles",
    }),
    removal.skippedStaff > 0 && t("iam.dashboard.people.assignFixed.skippedStaff", {
      count: removal.skippedStaff,
      defaultValue: "{{count}} Adge staff excluded",
    }),
    removal.skippedRoleless > 0 && t("iam.dashboard.people.assignFixed.skippedRoleless", {
      count: removal.skippedRoleless,
      defaultValue: "{{count}} already without roles",
    }),
  ].filter(Boolean);

  return (
    <>
      <div className="flex items-center gap-1.5 rounded-md bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
        <Users className="h-3.5 w-3.5 shrink-0" />
        <span>
          {t("iam.dashboard.people.assignFixed.removeAllSummary", {
            users: count,
            total: selectedUserIds.length,
            defaultValue: "{{users}} of {{total}} users will lose every role in every organization",
          })}
          {skips.map((skip) => <span key={String(skip)}> · {skip}</span>)}
        </span>
      </div>
      {isOverCap && (
        <p className="text-xs text-destructive">
          {t("iam.dashboard.people.assignFixed.removeAllOverCap", {
            max: MAX_USERS_ACCESS_BATCH,
            defaultValue: "Select at most {{max}} users at a time.",
          })}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="sm" onClick={onCancel}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button size="sm" variant="destructive" disabled={!canReview} onClick={() => setIsConfirming(true)}>
          {t("iam.dashboard.people.assignFixed.removeAllSubmit", {
            count,
            defaultValue: "Remove all roles from {{count}} users…",
          })}
        </Button>
      </div>
    </>
  );
}
